import React, { useState, useMemo, useEffect } from 'react'
import { TrendingUp, Users, DollarSign, Target, LucideIcon } from 'lucide-react'
import { PerformanceMetricCard } from './PerformanceMetricCard'
import { PerformanceTrendChart, TimeSeriesDataPoint } from './PerformanceTrendChart'
import { AnalyticsFilters, DateRangeOption, FilterDimension } from './AnalyticsFilters'
import { AnalyticsDetailTable, ColumnDefinition } from './AnalyticsDetailTable'

// Re-export TimeSeriesDataPoint for other dashboards
export type { TimeSeriesDataPoint } from './PerformanceTrendChart'

export interface MetricDefinition {
  key: string
  label: string
  value: string | number
  change?: string
  icon: any
  color: string
  useNormalization?: boolean // If false, chart shows raw values instead of normalized 0-100%
}

export interface DetailView<T = any> {
  id: string
  label: string
  icon: LucideIcon
  columns: ColumnDefinition<T>[]
  data: T[]
  keyExtractor: (row: T, index: number) => string | number
  defaultSortBy?: string
  defaultSortOrder?: 'asc' | 'desc'
  emptyMessage?: string
}

interface AnalyticsPerformanceDashboardProps {
  metrics: MetricDefinition[]
  timeSeriesData: TimeSeriesDataPoint[]
  title?: string
  dateRange?: string

  // Filter configuration
  enableDateFilter?: boolean
  dateRangeOptions?: DateRangeOption[]
  enableComparison?: boolean
  availableFilters?: FilterDimension[]

  // Detail views (new feature!)
  detailViews?: DetailView[]
  defaultDetailView?: string

  // Callbacks for filtering
  onDateRangeChange?: (range: string) => void
  onFilterChange?: (filterKey: string, values: string[]) => void
}

export function AnalyticsPerformanceDashboard({
  metrics,
  timeSeriesData,
  title = "Performance Overview",
  dateRange = "Last 16 months",
  enableDateFilter = true,
  dateRangeOptions = defaultDateRangeOptions,
  enableComparison = true,
  availableFilters = [],
  detailViews = [],
  defaultDetailView,
  onDateRangeChange,
  onFilterChange
}: AnalyticsPerformanceDashboardProps) {
  const [activeMetrics, setActiveMetrics] = useState<string[]>([])

  // Initialize activeMetrics when metrics load (fixes race condition)
  useEffect(() => {
    if (metrics.length > 0 && activeMetrics.length === 0) {
      setActiveMetrics([metrics[0].key])
    }
  }, [metrics, activeMetrics.length])

  // Filter state
  const [selectedDateRange, setSelectedDateRange] = useState(dateRangeOptions[0]?.value || 'last-12-months')
  const [comparisonEnabled, setComparisonEnabled] = useState(false)
  const [comparisonType, setComparisonType] = useState<'previous-period' | 'previous-year' | 'custom'>('previous-period')
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({})

  // Detail view state
  const [activeDetailView, setActiveDetailView] = useState<string | null>(
    defaultDetailView || (detailViews.length > 0 ? detailViews[0].id : null)
  )

  const toggleMetric = (metricKey: string) => {
    setActiveMetrics(prev => {
      if (prev.includes(metricKey)) {
        // Don't allow deselecting the last metric
        if (prev.length === 1) return prev
        return prev.filter(k => k !== metricKey)
      } else {
        return [...prev, metricKey]
      }
    })
  }

  // Apply date range filter to data
  const filteredTimeSeriesData = useMemo(() => {
    const option = dateRangeOptions.find(o => o.value === selectedDateRange)
    if (!option) return timeSeriesData

    // Convert days to approximate data points (assuming monthly data: 30 days = 1 month)
    const approximateDataPoints = Math.ceil(option.days / 30)

    // Take last N data points
    return timeSeriesData.slice(-approximateDataPoints)
  }, [timeSeriesData, selectedDateRange, dateRangeOptions])

  // Apply dimension filters to data
  const fullyFilteredData = useMemo(() => {
    let data = filteredTimeSeriesData

    // Apply active filters only if there are available filters configured
    if (availableFilters.length > 0) {
      Object.entries(activeFilters).forEach(([filterKey, values]) => {
        if (values.length > 0) {
          data = data.filter(point => {
            const pointValue = point[filterKey]
            // Only filter if the field exists in the data point
            if (pointValue !== undefined) {
              return values.includes(String(pointValue))
            }
            return true // Keep data point if field doesn't exist
          })
        }
      })
    }

    return data
  }, [filteredTimeSeriesData, activeFilters, availableFilters.length])

  // Prepare comparison data if enabled
  const comparisonData = useMemo(() => {
    if (!comparisonEnabled || !comparisonType) {
      console.log('[Comparison] Disabled or no type selected')
      return null
    }

    if (comparisonType === 'previous-period') {
      const currentLength = fullyFilteredData.length
      console.log('[Comparison] Current period length:', currentLength)

      // Find the starting index of filtered data in FILTERED dataset
      const firstFilteredDate = fullyFilteredData[0]?.date
      if (!firstFilteredDate) {
        console.log('[Comparison] No filtered data available')
        return null
      }

      // Find where the filtered period starts in the FULL unfiltered timeSeriesData
      // This is the key fix - we need to look at ALL available data, not just filtered
      const startIndexInFull = timeSeriesData.findIndex(d => d.date === firstFilteredDate)
      console.log('[Comparison] Start index in FULL dataset:', startIndexInFull, 'First date:', firstFilteredDate)
      console.log('[Comparison] Total available data points in FULL dataset:', timeSeriesData.length)

      // Check if we have enough previous data in the FULL dataset
      if (startIndexInFull < currentLength) {
        console.log('[Comparison] Not enough previous data. Need:', currentLength, 'Have:', startIndexInFull)
        console.log('[Comparison] Tip: Try selecting a later date range (e.g., last 6 months instead of last 12)')
        return null
      }

      // Get previous period data from FULL dataset with same length as current filtered period
      const previousData = timeSeriesData.slice(
        startIndexInFull - currentLength,
        startIndexInFull
      )

      console.log('[Comparison] Generated comparison data with', previousData.length, 'points')
      console.log('[Comparison] Current period:', fullyFilteredData.map(d => d.date).join(', '))
      console.log('[Comparison] Comparison period:', previousData.map(d => d.date).join(', '))
      return previousData
    }

    if (comparisonType === 'previous-year') {
      // For yearly comparison, we'd need 12 months offset
      // This requires proper date parsing which we'll skip for now
      console.log('[Comparison] Previous year comparison not yet implemented')
      return null
    }

    return null
  }, [comparisonEnabled, comparisonType, fullyFilteredData, timeSeriesData])

  const activeMetricConfigs = metrics
    .filter(m => activeMetrics.includes(m.key))
    .map(m => ({
      key: m.key,
      label: m.label,
      color: m.color,
      useNormalization: m.useNormalization
    }))

  const handleDateRangeChangeInternal = (range: string) => {
    setSelectedDateRange(range)
    if (onDateRangeChange) {
      onDateRangeChange(range)
    }
  }

  const handleFilterChangeInternal = (filterKey: string, values: string[]) => {
    const newFilters = { ...activeFilters, [filterKey]: values }
    setActiveFilters(newFilters)
    if (onFilterChange) {
      // Pass individual filter change for better performance
      onFilterChange(filterKey, values)
    }
  }

  const handleClearFilters = () => {
    // Clear each filter individually
    Object.keys(activeFilters).forEach(key => {
      if (onFilterChange) {
        onFilterChange(key, [])
      }
    })
    setActiveFilters({})
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-white">
            {title}
          </h2>
          <p className="text-brand-gray text-sm mt-1">{dateRange}</p>
        </div>
      </div>

      {/* Filters */}
      {(enableDateFilter || availableFilters.length > 0) && (
        <AnalyticsFilters
          selectedDateRange={selectedDateRange}
          onDateRangeChange={handleDateRangeChangeInternal}
          dateRangeOptions={dateRangeOptions}
          comparisonEnabled={comparisonEnabled}
          onComparisonToggle={setComparisonEnabled}
          comparisonType={comparisonType}
          onComparisonTypeChange={setComparisonType}
          availableFilters={availableFilters}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChangeInternal}
          onClearFilters={handleClearFilters}
        />
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {metrics.map((metric) => (
          <PerformanceMetricCard
            key={metric.key}
            title={metric.label}
            value={metric.value}
            change={metric.change}
            icon={metric.icon}
            color={metric.color}
            isActive={activeMetrics.includes(metric.key)}
            onClick={() => toggleMetric(metric.key)}
          />
        ))}
      </div>

      {/* Trend Chart */}
      <PerformanceTrendChart
        data={fullyFilteredData}
        activeMetrics={activeMetricConfigs}
        comparisonData={comparisonData}
        height={400}
      />

      {/* Detail Views */}
      {detailViews.length > 0 && (
        <div className="space-y-4">
          {/* Tab Navigation */}
          <div className="border-b border-brand-gray/10">
            <div className="flex gap-2 -mb-px">
              {detailViews.map((view) => {
                const Icon = view.icon
                return (
                  <button
                    key={view.id}
                    onClick={() => setActiveDetailView(view.id)}
                    className={`
                      flex items-center gap-2 px-4 py-3 text-sm font-medium
                      border-b-2 transition-colors
                      ${activeDetailView === view.id
                        ? 'border-brand-primary text-brand-primary'
                        : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray/30'
                      }
                    `}
                  >
                    <Icon size={16} />
                    {view.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Active Detail View */}
          {detailViews.map((view) => (
            activeDetailView === view.id && (
              <AnalyticsDetailTable
                key={view.id}
                columns={view.columns}
                data={view.data}
                keyExtractor={view.keyExtractor}
                defaultSortBy={view.defaultSortBy}
                defaultSortOrder={view.defaultSortOrder}
                emptyMessage={view.emptyMessage}
                rowsPerPage={10}
                showPagination={true}
                highlightOnHover={true}
              />
            )
          ))}
        </div>
      )}
    </div>
  )
}

// Default date range options (days converted to approximate months for monthly data)
const defaultDateRangeOptions: DateRangeOption[] = [
  { label: 'Últimos 3 meses', value: 'last-3-months', days: 90 },
  { label: 'Últimos 6 meses', value: 'last-6-months', days: 180 },
  { label: 'Ano atual', value: 'current-year', days: (new Date().getMonth() + 1) * 30 }, // Current year up to current month
  { label: 'Últimos 12 meses', value: 'last-12-months', days: 365 },
  { label: 'Últimos 16 meses', value: 'last-16-months', days: 480 },
  { label: 'Últimos 24 meses', value: 'last-24-months', days: 720 },
  { label: 'Todos os dados', value: 'all-time', days: 99999 }
]

// Example usage with fake data generator
export function generateFakeTimeSeriesData(months: number = 7): TimeSeriesDataPoint[] {
  const data: TimeSeriesDataPoint[] = []
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

  for (let i = 0; i < months; i++) {
    data.push({
      date: monthNames[i % 12],
      totalRevenue: 2000000 + Math.random() * 1000000,
      activeUsers: 1000 + Math.random() * 500,
      conversion: 15 + Math.random() * 10,
      satisfaction: 70 + Math.random() * 25
    })
  }

  return data
}

export const exampleMetrics: MetricDefinition[] = [
  {
    key: 'totalRevenue',
    label: 'Receita Total',
    value: 'R$ 2.8M',
    change: '+12.9%',
    icon: DollarSign,
    color: 'brand-primary'
  },
  {
    key: 'activeUsers',
    label: 'Usuários Ativos',
    value: '1,247',
    change: '+8.5%',
    icon: Users,
    color: 'brand-blue'
  },
  {
    key: 'conversion',
    label: 'Taxa de Conversão',
    value: '18.7%',
    change: '+2.3%',
    icon: TrendingUp,
    color: 'success'
  },
  {
    key: 'satisfaction',
    label: 'Satisfação',
    value: '89%',
    change: '+5.1%',
    icon: Target,
    color: 'warning'
  }
]
